import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Clock, Sparkles } from "lucide-react";
import SectionHeading from "../ui/SectionHeading.jsx";
import CTABanner from "../ui/CTABanner.jsx";
import Marquee from "../ui/Marquee.jsx";
import { Reveal, MaskLines, ImageReveal, VideoWithSound } from "../motion/Reveal.jsx";

const TRAIN_WORDS = ["Train.", "Play.", "Perform.", "Recover.", "Repeat."];
const BLUE_TENURE = { katargam: "12 Years", adajan: "15 Years", varachha: "25 Years" };

export default function ClubTemplate({
  club,
  seo,
  heroLines,
  heroCopy,
  introEyebrow,
  introLines,
  introPoints,
  signature,
  signatureEyebrow = "Signature Experiences",
  ctaLines,
  trainBg,
  testId,
}) {
  const [highlight, setHighlight] = useState(null);

  useEffect(() => {
    const onHighlight = (e) => {
      const anchor = e.detail?.anchor || "";
      if (anchor.startsWith("activity-")) {
        setHighlight(anchor);
        setTimeout(() => setHighlight((h) => (h === anchor ? null : h)), 5000);
      }
    };
    window.addEventListener("oneiros-highlight", onHighlight);
    return () => window.removeEventListener("oneiros-highlight", onHighlight);
  }, []);

  const slug = (name) => name.toLowerCase().replace(/\s+/g, "-");

  return (
    <>
      {/* HERO */}
      <section data-testid={`${testId}-hero`} className="mx-auto max-w-[1440px] px-6 pb-14 pt-36 lg:px-12 lg:pt-48">
        <SectionHeading eyebrow={club.eyebrow} lines={heroLines} copy={heroCopy} />
        <Reveal delay={0.2} className="mt-10 flex flex-col gap-4 sm:flex-row">
          <Link
            data-testid={`${testId}-enquire-btn`}
            to="/contact"
            className="group inline-flex w-fit items-center gap-3 rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-dark"
          >
            Enquire Now
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            data-testid={`${testId}-clubs-link`}
            to="/our-clubs"
            className="inline-flex w-fit items-center rounded-full border border-navy/20 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-navy transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-white"
          >
            All Clubs
          </Link>
        </Reveal>
      </section>
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <ImageReveal src={club.image} alt={`Oneiros ${club.shortName} — ${club.positioning}`} className="aspect-[16/10] w-full md:aspect-[21/9]" />
      </div>

      {/* USP BAND */}
      <section data-testid={`${testId}-usp-band`} className="grain bg-navy py-16 text-white lg:py-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <Reveal>
            <p className="eyebrow flex items-center gap-3">
              <Sparkles className="h-4 w-4" /> {club.uspLabel}
            </p>
          </Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {club.highlights.map((h, i) => (
              <Reveal key={h} delay={i * 0.07}>
                <div data-testid={`${testId}-usp-${i}`} className="flex h-full items-start gap-3 border border-brand/40 bg-white/[0.04] p-5">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  <p className="text-sm font-semibold leading-snug text-white/90">{h}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <SectionHeading eyebrow={introEyebrow} lines={introLines} />
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <p className="text-base leading-relaxed text-mute md:text-lg">{club.description}</p>
            </Reveal>
            <ul className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {introPoints.map((p, i) => (
                <Reveal key={p} delay={0.1 + i * 0.06}>
                  <li data-testid={`${testId}-intro-point-${i}`} className="flex items-center gap-4 border-t border-hairline py-4">
                    <span className="font-display text-xl text-brand">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm font-semibold uppercase tracking-[0.12em] text-navy/85">{p}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ACTIVITIES */}
      <section className="border-y border-hairline bg-mist py-24 lg:py-32">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <SectionHeading eyebrow="Activities" lines={["Pick Your Activity.", "I'll Take You There."]} copy={`Activities at ${club.shortName} Club — choose one and our concierge will highlight it for you.`} />
          <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
            {club.activities.map((a, i) => {
              const anchorId = `activity-${slug(a.name)}`;
              return (
                <Reveal key={a.name} delay={i * 0.05}>
                  <Link
                    id={anchorId}
                    data-testid={`${testId}-activity-${slug(a.name)}`}
                    to="/contact"
                    className={`group block rounded-sm transition-all duration-300 ${highlight === anchorId ? "z-10 ring-2 ring-brand ring-offset-4 ring-offset-mist" : ""}`}
                  >
                    <div className="overflow-hidden">
                      <img src={a.image} alt={`${a.name} at Oneiros ${club.shortName}`} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <p className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-navy transition-colors group-hover:text-brand">{a.name}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SIGNATURE EXPERIENCES */}
      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <SectionHeading eyebrow={signatureEyebrow} lines={[`${club.shortName}`, "Signature", "Experiences."]} />
        <div className="mt-16 flex flex-col gap-20 lg:gap-28">
          {signature.map((s, i) => {
            const ratio = s.ratio || "16/10";
            const frame = { "9/16": "aspect-[9/16]", "16/9": "aspect-video", "1/1": "aspect-square" }[ratio] || "aspect-[16/10]";
            const col = ratio === "9/16" ? "lg:col-span-4" : ratio === "1/1" ? "lg:col-span-5" : "lg:col-span-7";
            const mediaClass = `${col} ${frame} w-full ${i % 2 === 1 ? "lg:order-2" : ""}`;
            return (
              <article key={s.title} data-testid={`${testId}-signature-${i}`} className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
                {s.video ? (
                  <VideoWithSound src={s.video} poster={s.poster} testId={`${testId}-signature-video-${i}`} className={mediaClass} />
                ) : (
                  <ImageReveal src={s.image} alt={s.title} className={mediaClass} />
                )}
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <Reveal>
                    <p className="font-display text-4xl text-faint">{String(i + 1).padStart(2, "0")}</p>
                  </Reveal>
                  <MaskLines lines={[s.title]} className="mt-4 font-display text-3xl leading-[1.1] text-navy sm:text-4xl" />
                  <Reveal delay={0.1}>
                    <p className="mt-4 max-w-md text-sm leading-relaxed text-mute md:text-base">{s.copy}</p>
                  </Reveal>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* TRAIN PLAY BAND */}
      <section className={`relative overflow-hidden py-20 text-white lg:py-24 ${trainBg ? "" : "grain bg-navy-obsidian"}`}>
        {trainBg && (
          <>
            <img src={trainBg} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-navy-obsidian/85" />
          </>
        )}
        <div className="relative z-10 mx-auto flex max-w-[1440px] flex-wrap items-center justify-center gap-x-8 gap-y-4 px-6 text-center lg:px-12">
          {TRAIN_WORDS.map((w, i) => (
            <Reveal key={w} delay={i * 0.08}>
              <span className="flex items-center gap-8 font-display text-3xl sm:text-4xl lg:text-5xl">
                <span className={trainBg ? `drop-shadow-[0_2px_14px_rgba(0,0,0,0.65)] ${i % 2 === 0 ? "text-white" : "text-brand"}` : ""}>
                  {w}
                </span>
                {i < TRAIN_WORDS.length - 1 && <span className="hidden h-2 w-2 rounded-full bg-brand sm:inline-block" />}
              </span>
            </Reveal>
          ))}
        </div>
        <p className="relative z-10 mx-auto mt-8 max-w-xl px-6 text-center text-sm font-semibold leading-relaxed text-white/80 [text-shadow:0_1px_10px_rgba(0,0,0,0.7)]">
          A complete lifestyle club for the entire family — from high-performance fitness to sports, group activities and wellness programs.
        </p>
      </section>

      {/* BLUE BAND */}
      <section className="grain bg-royal py-20 text-white lg:py-24">
        <div className="mx-auto flex max-w-[1440px] flex-col items-start gap-8 px-6 lg:flex-row lg:items-center lg:justify-between lg:px-12">
          <div>
            <Reveal>
              <p className="eyebrow flex items-center gap-3">
                <span className="inline-block h-[2px] w-8 bg-brand" /> Blue Membership
              </p>
            </Reveal>
            <MaskLines lines={[`${BLUE_TENURE[club.id]} of Fitness.`, "One Family Promise."]} className="mt-5 font-display text-3xl leading-[1.1] sm:text-4xl lg:text-5xl" />
          </div>
          <Reveal delay={0.15}>
            <Link
              data-testid={`${testId}-blue-cta`}
              to="/blue-membership"
              className="group inline-flex items-center gap-3 rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-dark"
            >
              Explore Blue Membership
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* INFO STRIP */}
      <section className="border-b border-hairline">
        <div className="mx-auto grid max-w-[1440px] gap-6 px-6 py-12 text-sm md:grid-cols-3 lg:px-12">
          <p className="flex items-start gap-3 text-body">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            {club.address ? (
              <a href={club.mapUrl || "#"} target="_blank" rel="noopener noreferrer" className="hover:text-brand">{club.address}</a>
            ) : (
              <span className="italic text-mute">{club.addressNote}</span>
            )}
          </p>
          <p className="flex items-start gap-3 text-body">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            {club.timings ? `Timings: ${club.timings}` : <span className="italic text-mute">Timings updating soon</span>}
          </p>
          <p className="flex items-start gap-3 text-body">
            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            {club.phone ? <a data-testid={`${testId}-phone`} href={club.phoneHref} className="hover:text-brand">{club.phone}</a> : <span className="italic text-mute">Phone updating soon</span>}
          </p>
        </div>
      </section>

      <Marquee items={["Fitness", "Sports", "Wellness", "Community", "Performance", "Lifestyle"]} slow dark />
      <div className="h-px w-full bg-navy-obsidian" />
      <CTABanner
        eyebrow={club.name}
        lines={ctaLines}
        primary={{ label: "Enquire Now", to: "/contact" }}
        secondary={{ label: "Visit a Club", to: "/our-clubs" }}
        testId={`${testId}-final-cta`}
      />
    </>
  );
}
